import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import { 
  ArrowLeft, UploadCloud, FileText, Trash2, 
  Download, Sparkles, AlertTriangle, CheckCircle2, Circle, Eye,
  School, Book, Calendar
} from 'lucide-react'
import './styles.scss'

export default function StudentDocumentDetails() {
  const { appId, documentType } = useParams()
  const navigate = useNavigate()
  
  const [appContext, setAppContext] = useState(null)
  const [checklistItem, setChecklistItem] = useState(null)
  const [document, setDocument] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    fetchData()
  }, [appId, documentType])

  useEffect(() => {
    let interval;
    if (document && (!document.aiInsight || !document.aiInsight.decisionStatus)) {
      // Poll every 3 seconds if AI review is still pending
      interval = setInterval(() => {
        pollDocumentStatus();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    }
  }, [document])

  const pollDocumentStatus = async () => {
    try {
      const docRes = await axiosInstance.get(`/api/documents/application/${appId}`)
      const docs = docRes.data || []
      const updatedDoc = docs.find(d => d.documentType === documentType)
      if (updatedDoc) {
        setDocument(updatedDoc)
      }
    } catch (e) {
      console.error('Failed to poll document status', e)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Fetch application context to get the fully populated DTO
      const appRes = await axiosInstance.get(`/api/applications/me`)
      setAppContext(appRes.data?.data || appRes.data)

      // Fetch specific checklist item
      const chkRes = await axiosInstance.get(`/api/checklists/application/${appId}`)
      const items = chkRes.data || []
      const item = items.find(i => i.documentType === documentType)
      setChecklistItem(item)

      // Fetch documents for this application and find this specific type
      const docRes = await axiosInstance.get(`/api/documents/application/${appId}`)
      const docs = docRes.data || []
      const doc = docs.find(d => d.documentType === documentType)
      setDocument(doc)

    } catch (e) {
      console.error('Failed to load document details', e)
    } finally {
      setLoading(false)
    }
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file || !appId || !documentType) return

    setUploading(true)
    
    const formData = new FormData()
    formData.append('file', file)
    formData.append('applicationId', appId)
    formData.append('documentType', documentType)

    try {
      await axiosInstance.post('/api/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      await fetchData() // Refresh to get the AI feedback
    } catch (err) {
      console.error('Upload failed', err)
      alert('Upload failed: ' + (err.response?.data?.message || err.message))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async () => {
    if (!document) return
    if (!window.confirm('Are you sure you want to delete this document?')) return
    
    try {
      await axiosInstance.delete(`/api/documents/${document.id}`)
      setDocument(null)
      await fetchData()
    } catch (e) {
      alert('Failed to delete document')
    }
  }

  const handleDownload = async () => {
    if (!document) return
    try {
      const res = await axiosInstance.get(`/api/documents/${document.id}/download`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = window.document.createElement('a')
      link.href = url
      link.setAttribute('download', document.originalFileName || 'document.pdf')
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      // Note: We don't revoke the object URL immediately because it might still be downloading
    } catch (e) {
      alert('Failed to download document')
    }
  }

  const handlePreview = async () => {
    if (!document) return
    try {
      const res = await axiosInstance.get(`/api/documents/${document.id}/download`, { responseType: 'blob' })
      // Use the blob's actual MIME type returned by the server, fallback to pdf
      const fileBlob = new Blob([res.data], { type: res.data.type || 'application/pdf' })
      const url = window.URL.createObjectURL(fileBlob)
      window.open(url, '_blank')
      
      // We should ideally revoke the URL later, but for a new tab it's usually fine
    } catch (e) {
      alert('Failed to preview document')
    }
  }

  const handleDeleteFile = async () => {
    if (!document) return
    setShowDeleteModal(true)
  }

  const confirmDeleteFile = async () => {
    setShowDeleteModal(false)
    try {
      setUploading(true)
      await axiosInstance.delete(`/api/documents/${document.id}`)
      setDocument(null)
      // Refresh the checklist items
      fetchData()
    } catch (e) {
      alert('Failed to delete document. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return ''
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB • `
    }
    return `${(bytes / 1024).toFixed(1)} KB • `
  }

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    )
  }

  if (!checklistItem) {
    return (
      <div className="page-container student-doc-details">
        <div className="doc-details-header">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={20} /> Back
          </button>
        </div>
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <AlertTriangle size={48} className="empty-icon" />
          <h3>Document Not Found</h3>
          <p>We couldn't find the checklist item for this document type.</p>
        </div>
      </div>
    )
  }

  const isUploaded = !!document
  const isCompleted = checklistItem.status === 'Completed' || (document && document.humanReviewStatus === 'VERIFIED')
  const isRejected = document?.humanReviewStatus === 'REJECTED' || document?.aiInsight?.decisionStatus === 'REJECTED'

  return (
    <div className="page-container student-doc-details">
      <div className="doc-details-header">
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} /> Back
        </button>
      </div>

      <PageHeader 
        title={checklistItem.title}
        subtitle={(appContext?.data?.universityName || appContext?.universityName) ? `Application Requirement` : ''}
        universityName={appContext?.data?.universityName || appContext?.universityName}
      />

      {/* Hidden File Input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileChange}
      />

      <div className="doc-content-grid">
        <div className="main-panel">
          {/* Application Context Card */}
          {(appContext?.data || appContext) && (appContext?.data?.universityName || appContext?.universityName) && (() => {
            const data = appContext.data || appContext;
            return (
              <div className="glass-panel app-context-card" style={{ marginBottom: '1.5rem', padding: '1.5rem', flexDirection: 'column', gap: '1rem' }}>
                <div className="app-context-item">
                  <School size={18} color="#1d4ed8" />
                  <span className="item-label-primary">{data.universityName}</span>
                </div>
                <div className="app-context-item">
                  <Book size={18} color="#64748b" />
                  <span className="item-label-secondary">{data.programName}</span>
                </div>
                <div className="app-context-item">
                  <Calendar size={18} color="#64748b" />
                  <span className="item-label-secondary">{data.intakeName}</span>
                </div>
              </div>
            );
          })()}

          {/* Instructions Card */}
          <div className="glass-panel instruction-card">
            <div className="card-header">
              <div className="status-icon">
                {isCompleted ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
              </div>
              <div>
                <h3>Instructions {!checklistItem.required && <span className="tag-optional">Optional</span>}</h3>
              </div>
            </div>
            <div className="card-body">
              <p>{checklistItem.instructions || checklistItem.description}</p>
            </div>
          </div>
        </div>

        {/* Sidebar / Reviews */}
        <div className="sidebar-panel">
          <div className="glass-panel status-card">
            <h3>Review Status</h3>
            
            {isUploaded ? (
              <div className="reviews-container">
                {/* AI Feedback */}
                {document.aiInsight?.decisionStatus ? (
                  <div className={`ai-feedback ${document.aiInsight.decisionStatus.toLowerCase()}`}>
                    <div className="feedback-header">
                      <div className="bot-label"><Sparkles size={14} /> AI Review</div>
                      <strong>{document.aiInsight.decisionStatus}</strong>
                    </div>
                    {document.aiInsight.decisionDescription && (
                      <div className="feedback-body">
                        <p>{document.aiInsight.decisionDescription}</p>
                        {document.aiInsight.decisionStatus === 'REJECTED' && (
                          <div className="action-required">
                            <AlertTriangle size={14} /> Please replace the file with a clear copy.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pending-review">
                    <Sparkles size={16} /> <span>AI is analyzing your document...</span>
                  </div>
                )}

                {/* Human Review Status */}
                {(!document.humanReviewStatus || document.humanReviewStatus === 'PENDING' || document.humanReviewStatus === 'IN_PROGRESS') ? (
                  <div className="pending-review">
                    <Circle size={16} /> <span>Human Review: <strong>In Progress</strong></span>
                  </div>
                ) : (
                  <div className={`human-feedback ${document.humanReviewStatus.toLowerCase()}`}>
                    <span>Human Review: <strong>{document.humanReviewStatus}</strong></span>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-status">
                <p>Upload a document to begin the review process.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload / Preview Area (Full Width) */}
      <div className="glass-panel document-upload-card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Document File</h3>
          {isUploaded && (
            <div className={`document-status-badge ${isRejected ? 'rejected' : isCompleted ? 'completed' : 'under-review'}`}>
              {isRejected ? 'Needs Replacement' : isCompleted ? 'Completed' : 'Under Review'}
            </div>
          )}
        </div>
        
        {!isUploaded ? (
          <div className="upload-dropzone" onClick={handleUploadClick}>
            {uploading ? (
              <div className="spinner-large" />
            ) : (
              <>
                <UploadCloud size={48} className="upload-icon" />
                <h4>Click to upload file</h4>
                <p>PDF, JPG, or PNG (Max 5MB)</p>
              </>
            )}
          </div>
        ) : (
          <div className="uploaded-file-preview" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="file-info-large">
              <div className="file-icon"><FileText size={32} /></div>
              <div className="file-details">
                <strong>{document.originalFileName || `${documentType}.pdf`}</strong>
                <span>
                  {formatFileSize(document.size || document.fileSize)}
                  Uploaded on {new Date(document.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="file-actions">
              <button 
                className="btn btn-primary"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                <UploadCloud size={18} />
                Replace File
              </button>

              <button 
                className="btn btn-outline"
                onClick={handleDownload}
                disabled={uploading}
              >
                <Download size={18} />
                Download
              </button>
              
              <button 
                className="btn btn-outline"
                onClick={handlePreview}
                disabled={uploading}
              >
                <Eye size={18} />
                Preview
              </button>

              <button 
                className="btn btn-outline"
                style={{ borderColor: '#fecaca', color: '#dc2626' }}
                onClick={handleDeleteFile}
                disabled={uploading}
              >
                <Trash2 size={18} />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteFile}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  )
}
